/**
 * User Testing Page component
 * A simplified interface for user testing with email input, tabs for viewing data, and feedback collection
 */

import {
  AlertCircle,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Info,
  LayoutGrid,
  Loader2,
  LogOut,
  MessageSquare,
  Search,
  Target,
  ThumbsDown,
  ThumbsUp,
  User,
} from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getHomeUrl, getLanguageSwitchUrl } from '../config/siteMode';
import { useFeedbackPrompt } from '../hooks/useFeedbackPrompt';
import {
  type CustomerHistoryItem,
  flattenRecommendationsByProfile,
  logoutCustomer,
  type RecommendationResult,
  submitProfileRating,
} from '../services/api';
import type { ThumbsRating } from '../types';
import { getDisplayAuthor } from '../utils/displayAuthor';
import { mergeRecommendationTypes } from '../utils/recommendationTypes';
import { resolveTourTarget } from '../utils/tourTarget';
import BookGrid, { type BookData } from './BookGrid';
import ExperienceFeedbackModal from './ExperienceFeedbackModal';

import FeedbackForm, { type FeedbackData } from './FeedbackForm';
import FeedbackSideTab, { type FeedbackChoice } from './FeedbackSideTab';
import IndigoLogo from './IndigoLogo';
import OnboardingTour, { hasSeenTour } from './OnboardingTour';
import ProfileCarouselPage from './ProfileCarouselPage';

interface UserTestingPageProps {
  onEmailSubmit: (email: string) => Promise<void>;
  onFeedbackSubmit: (feedback: FeedbackData) => Promise<void>;
  result: RecommendationResult | null;
  isLoading: boolean;
  error: string | null;
  infoMessage?: string | null;
  feedbackSubmitting: boolean;
  feedbackError: string | null;
  feedbackSuccess: boolean;
  email: string;
  setEmail: (email: string) => void;
  setSubmittedEmail: (email: string) => void;
  shopifyCustomerId?: string;
  customerHistory?: CustomerHistoryItem[];
  customerHistoryLoading?: boolean;
  /** Customer-facing CDN experience: no customer selector, no prices, no Recommendations tab. */
  cdnMode?: boolean;
  /** Signed-in customer's first name (CDN mode), shown as a greeting in the header. */
  customerFirstName?: string;
}

type TabType = 'customer' | 'history' | 'recommendations' | 'foryou' | 'feedback';

// Build the Indigo cover image URL for a compact book thumbnail.
const getProfileBookImageUrl = (book: BookData): string => {
  const identifier = book.isbn || book.sku || book.book_id;
  if (!identifier) {
    return '/placeholder-book.png';
  }
  // Clean the identifier (the endpoint 404s on hyphenated ids)
  const cleanId = identifier.replace(/-/g, '');
  // Compact 40px thumbnail; 120x180 covers retina with room to spare.
  return `https://dynamic.indigoimages.ca/v1/books/books/${cleanId}/1.jpg?width=120&maxHeight=180&quality=85`;
};

// Compact book row used inside an expanded profile card.
const ProfileBookRow: React.FC<{ book: BookData }> = ({ book }) => {
  const { t } = useTranslation();
  const [imageError, setImageError] = useState(false);
  const imageUrl = getProfileBookImageUrl(book);
  const displayAuthor = getDisplayAuthor(book.author, book.publisher);

  return (
    <div className="flex items-center gap-3 bg-white/70 rounded-lg p-2 border border-white">
      <div className="w-10 h-14 flex-shrink-0 rounded overflow-hidden bg-gray-100">
        {!imageError ? (
          <img
            src={imageUrl}
            alt={book.title || t('book.coverAlt')}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-indigo-brand-off-white text-lg">
            📚
          </div>
        )}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-800 line-clamp-2" title={book.title}>
          {book.title || t('book.unknownTitle')}
        </p>
        {displayAuthor && <p className="text-xs text-gray-500 line-clamp-1">{displayAuthor}</p>}
      </div>
    </div>
  );
};

// Profile Card Component - extracted to reduce cognitive complexity
interface ProfileCardProps {
  profile: any;
  index: number;
  isDominant: boolean;
  currentRating: ThumbsRating;
  onRatingChange: (profileKey: string, rating: ThumbsRating) => void;
  books: BookData[];
}

const ProfileCard: React.FC<ProfileCardProps> = ({
  profile,
  index,
  isDominant,
  currentRating,
  onRatingChange,
  books,
}) => {
  const { t } = useTranslation();
  const [showBooks, setShowBooks] = useState(false);
  const profileKey = profile.name || `profile-${index}`;
  const bookCount = profile.book_count || 0;
  const canExpandBooks = books.length > 0;
  const toggleBooksLabel = showBooks ? t('profile.hideBooks') : t('profile.showBooks');
  const expandBooksTitle = canExpandBooks ? toggleBooksLabel : undefined;

  const cardStyles = isDominant
    ? 'bg-amber-50/50 border-indigo-brand-border'
    : 'bg-indigo-brand-off-white border-indigo-brand-border';

  const textStyles = isDominant ? 'text-indigo-brand-text' : 'text-indigo-brand-text';
  const borderStyles = isDominant ? 'border-indigo-brand-border' : 'border-indigo-brand-border';
  const badgeStyles = isDominant
    ? 'bg-amber-100 text-amber-800'
    : 'bg-gray-100 text-indigo-brand-text';
  const charStyles = isDominant
    ? 'text-amber-700 border-indigo-brand-border'
    : 'text-indigo-brand-text/70 border-indigo-brand-border';

  return (
    <div className={`p-5 rounded-sm border flex flex-col ${cardStyles}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center flex-wrap gap-x-2 gap-y-1 min-w-0">
          <h5 className={`font-heading font-bold text-lg break-words ${textStyles}`}>
            {profile.name || t('profile.unknownProfile')}
          </h5>
          {isDominant && (
            <span className="px-2 py-0.5 bg-yellow-400 text-yellow-900 text-xs rounded-full font-bold uppercase whitespace-nowrap">
              {t('profile.dominant')}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => canExpandBooks && setShowBooks((prev) => !prev)}
          disabled={!canExpandBooks}
          aria-expanded={showBooks}
          title={expandBooksTitle}
          className={`flex items-center gap-1 px-2 py-1 text-xs rounded-full font-medium transition-opacity flex-shrink-0 whitespace-nowrap ${badgeStyles} ${
            canExpandBooks ? 'cursor-pointer hover:opacity-80' : 'cursor-default'
          }`}
        >
          {t('profile.bookCount', { count: bookCount })}
          {canExpandBooks && (showBooks ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
        </button>
      </div>
      <p className="text-sm text-gray-700 mb-3">
        {profile.description || t('profile.noDescription')}
      </p>
      {profile.characteristics && profile.characteristics.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {profile.characteristics.slice(0, 3).map((char: string) => (
            <span
              key={char}
              className={`px-2 py-0.5 bg-white text-xs rounded-full border ${charStyles}`}
            >
              {char}
            </span>
          ))}
        </div>
      )}
      {profile.themes && profile.themes.length > 0 && (
        <div className={`mt-2 pt-2 border-t ${borderStyles}`}>
          <span className="text-xs font-medium text-gray-600">{t('profile.themesLabel')} </span>
          <span className="text-xs text-gray-700">{profile.themes.slice(0, 5).join(', ')}</span>
        </div>
      )}
      {profile.genres && profile.genres.length > 0 && (
        <div className="mt-1">
          <span className="text-xs font-medium text-gray-600">{t('profile.genresLabel')} </span>
          <span className="text-xs text-gray-700">{profile.genres.slice(0, 3).join(', ')}</span>
        </div>
      )}
      {/* Expandable list of books that make up this profile */}
      {showBooks && canExpandBooks && (
        <div className={`mt-3 pt-3 border-t ${borderStyles}`}>
          <p className="text-xs font-medium text-gray-600 mb-2">
            {t('profile.booksInProfile', { count: books.length })}
          </p>
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {books.map((book, bookIndex) => (
              <ProfileBookRow
                key={book.book_id || book.isbn || book.sku || `profile-book-${bookIndex}`}
                book={book}
              />
            ))}
          </div>
        </div>
      )}
      {/* Profile Rating Buttons */}
      <div className={`mt-auto pt-3 border-t ${borderStyles}`}>
        <p className="text-xs text-gray-500 mb-2">{t('profile.describesYou')}</p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() =>
              onRatingChange(profileKey, currentRating === 'thumbs_up' ? null : 'thumbs_up')
            }
            className={`flex-1 flex items-center justify-center gap-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
              currentRating === 'thumbs_up'
                ? 'bg-green-500 text-white'
                : `bg-white text-gray-600 hover:bg-green-100 hover:text-green-600 border ${borderStyles}`
            }`}
          >
            <ThumbsUp size={16} />
            {t('common.yes')}
          </button>
          <button
            type="button"
            onClick={() =>
              onRatingChange(profileKey, currentRating === 'thumbs_down' ? null : 'thumbs_down')
            }
            className={`flex-1 flex items-center justify-center gap-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
              currentRating === 'thumbs_down'
                ? 'bg-red-500 text-white'
                : `bg-white text-gray-600 hover:bg-red-100 hover:text-red-600 border ${borderStyles}`
            }`}
          >
            <ThumbsDown size={16} />
            {t('common.no')}
          </button>
        </div>
      </div>
    </div>
  );
};

const UserTestingPage: React.FC<UserTestingPageProps> = ({
  onEmailSubmit,
  onFeedbackSubmit,
  result,
  isLoading,
  error,
  infoMessage,
  feedbackSubmitting,
  feedbackError,
  feedbackSuccess,
  email,
  setEmail,
  setSubmittedEmail,
  shopifyCustomerId,
  customerHistory = [],
  customerHistoryLoading = false,
  cdnMode = false,
  customerFirstName,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>('foryou');
  const [feedbackChoice, setFeedbackChoice] = useState<FeedbackChoice | null>(null);
  const [isTourRunning, setIsTourRunning] = useState(false);
  const [bookRatings, setBookRatings] = useState<Record<string, ThumbsRating>>({});
  const [historyRatings, setHistoryRatings] = useState<Record<string, ThumbsRating>>({});
  const [showPurchasedOnly, setShowPurchasedOnly] = useState(false);
  const [profileRatings, setProfileRatings] = useState<Record<string, ThumbsRating>>({});
  const [giftStatus, setGiftStatus] = useState<Record<string, boolean | null>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      const emailToSubmit = email.trim();
      setSubmittedEmail(emailToSubmit); // Store the submitted email
      setHasSubmitted(true);
      setBookRatings({}); // Reset ratings for new search
      setHistoryRatings({}); // Reset history ratings for new search
      setProfileRatings({}); // Reset profile ratings for new search
      setGiftStatus({}); // Reset gift status for new search
      await onEmailSubmit(emailToSubmit);
      setActiveTab('foryou'); // Navigate to For You tab after getting recommendations
    }
  };

  const handleRatingChange = (bookId: string, rating: ThumbsRating) => {
    setBookRatings((prev) => ({
      ...prev,
      [bookId]: rating,
    }));
  };

  const handleHistoryRatingChange = (bookId: string, rating: ThumbsRating) => {
    setHistoryRatings((prev) => ({
      ...prev,
      [bookId]: rating,
    }));
  };

  const handleProfileRatingChange = (profileName: string, rating: ThumbsRating) => {
    setProfileRatings((prev) => ({
      ...prev,
      [profileName]: rating,
    }));
    if (rating && result?.shopify_customer_id) {
      submitProfileRating({
        profile_name: profileName,
        rating,
        shopify_customer_id: result.shopify_customer_id,
      }).catch(() => {});
    }
  };

  const handleGiftStatusChange = (bookId: string, isGift: boolean | null) => {
    setGiftStatus((prev) => ({
      ...prev,
      [bookId]: isGift,
    }));
  };

  // Helper function to extract book data from different formats
  const extractBookData = (bookObj: any): BookData => {
    if (!bookObj) return {};
    return {
      book_id: bookObj.BOOK_ID || bookObj.book_id,
      isbn: bookObj.ISBN || bookObj.isbn,
      sku: bookObj.SKU || bookObj.sku,
      title: bookObj.TITLE || bookObj.title,
      author: bookObj.AUTHOR || bookObj.author,
      price: bookObj.LIST_PRICE || bookObj.price,
      language: bookObj.LANGUAGE || bookObj.language,
      publisher: bookObj.PUBLISHER || bookObj.publisher,
      theme1: bookObj.THEME1 || bookObj.theme1,
      theme2: bookObj.THEME2 || bookObj.theme2,
      is_canadian_author: bookObj.IS_CANADIAN_AUTHOR || bookObj.is_canadian_author,
      publication_date: bookObj.PUBLICATION_DATE || bookObj.publication_date,
      popularity_score: bookObj.POPULARITY_SCORE || bookObj.popularity_score,
      page_count: bookObj.PAGE_COUNT || bookObj.page_count,
      indigo_recommendation_types: mergeRecommendationTypes(bookObj),
    };
  };

  // Recommendations tab: prefer the ranked flat `recommendations` list (full bench).
  // Fall back to flattening recommendations_by_profile for older cached docs.
  const getRecommendationBooks = (): BookData[] => {
    if (!result) return [];

    const ranked = result.recommendations ?? [];
    const flat =
      ranked.length > 0
        ? [...ranked].sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity))
        : flattenRecommendationsByProfile(result.recommendations_by_profile);
    if (flat.length === 0) return [];

    return flat.map((rec: any, index: number) => {
      // Handle different book data formats from backend
      // Format 1: rec.book (transformed format with lowercase keys)
      // Format 2: rec.b2 (raw format with uppercase keys)
      // Format 3: rec.b (older format with uppercase keys)
      let book: BookData = {};

      if (rec.book && Object.keys(rec.book).length > 0) {
        book = extractBookData(rec.book);
        // If book exists but doesn't have title/author, try fallback formats
        if (!book.title && !book.author && (rec.b2 || rec.b)) {
          book = extractBookData(rec.b2 || rec.b);
        }
      } else if (rec.b2) {
        book = extractBookData(rec.b2);
      } else if (rec.b) {
        book = extractBookData(rec.b);
      }

      // Collect themes from rec.themes array or book.theme1/theme2 properties
      const recThemes = rec.themes || [];
      const bookThemes = [book.theme1, book.theme2].filter(Boolean) as string[];
      const allThemes = Array.from(new Set([...recThemes, ...bookThemes]));

      // Generate a unique ID using index as fallback
      const uniqueId = book.isbn || book.sku || book.book_id || `rec-${index}`;

      return {
        book_id: uniqueId,
        isbn: book.isbn,
        sku: book.sku,
        title: book.title,
        author: book.author,
        price: book.price,
        language: book.language || rec.language,
        publisher: book.publisher,
        themes: allThemes,
        genres: rec.genres || [],
        recommendation_score: rec.recommendation_score,
        explanation: rec.explanation,
        is_canadian_author: rec.is_canadian_author || book.is_canadian_author,
        sources: rec.sources || [],
        publication_date: book.publication_date,
        popularity_score: book.popularity_score,
        page_count: book.page_count,
        indigo_recommendation_types: book.indigo_recommendation_types,
      };
    });
  };

  const getHistoryBooks = (): BookData[] => {
    if (!customerHistory || customerHistory.length === 0) return [];

    return customerHistory
      .filter((item) => !showPurchasedOnly || item.interaction_type === 'PURCHASED')
      .map((item, index) => {
        const themes = [item.theme1, item.theme2].filter(Boolean) as string[];
        const uniqueId = item.isbn || item.sku || item.book_id || `history-${index}`;

        return {
          book_id: uniqueId,
          isbn: item.isbn,
          sku: item.sku,
          title: item.title,
          author: item.author,
          price: item.price,
          language: item.language,
          publisher: item.publisher,
          themes,
          genres: item.genres || [],
          is_canadian_author: item.is_canadian_author,
          interaction_type: item.interaction_type,
          purchase_date: item.last_purchase_date,
        };
      });
  };

  // Lookup of customer history books keyed by book_id so profiles can resolve
  // their member books (book_ids) into rich book data with covers.
  const historyBooksById = useMemo(() => {
    const map = new Map<string, BookData>();
    for (const item of customerHistory) {
      if (!item.book_id) continue;
      const themes = [item.theme1, item.theme2].filter(Boolean) as string[];
      map.set(item.book_id, {
        book_id: item.book_id,
        isbn: item.isbn,
        sku: item.sku,
        title: item.title,
        author: item.author,
        price: item.price,
        language: item.language,
        publisher: item.publisher,
        themes,
        genres: item.genres || [],
        is_canadian_author: item.is_canadian_author,
        interaction_type: item.interaction_type,
        purchase_date: item.last_purchase_date,
      });
    }
    return map;
  }, [customerHistory]);

  // Resolve a profile's member books, preferring full history data and falling
  // back to the titles supplied by the backend when history isn't loaded.
  const getProfileBooks = (profile: any): BookData[] => {
    const bookIds: string[] = profile.book_ids || [];
    const bookTitles: string[] = profile.book_titles || [];
    if (bookIds.length > 0) {
      return bookIds.map(
        (id, i) => historyBooksById.get(id) || { book_id: id, title: bookTitles[i] }
      );
    }
    return bookTitles.map((title, i) => ({ book_id: `title-${i}`, title }));
  };

  const allTabs = [
    { id: 'foryou' as TabType, label: t('tabs.forYou'), icon: LayoutGrid },
    { id: 'customer' as TabType, label: t('tabs.customerProfile'), icon: User },
    { id: 'history' as TabType, label: t('tabs.customerHistory'), icon: BookOpen },
    { id: 'recommendations' as TabType, label: t('tabs.recommendations'), icon: Target },
    { id: 'feedback' as TabType, label: t('tabs.feedback'), icon: MessageSquare },
  ];
  // Everything except the curated carousels is an internal testing view. The
  // profile tab duplicated what each carousel header already says, and the
  // feedback form moved into the side tab, so customers are left with a single
  // view — which is why the tab bar itself is hidden below.
  const internalOnlyTabs: TabType[] = ['recommendations', 'history', 'customer', 'feedback'];
  const tabs = cdnMode ? allTabs.filter((tab) => !internalOnlyTabs.includes(tab.id)) : allTabs;
  const showTabBar = tabs.length > 1;

  // Which carousel the tour explains. Null when nothing is worth spotlighting,
  // in which case the tour is skipped entirely.
  const tourTarget = useMemo(() => resolveTourTarget(result), [result]);

  // Start the tour once the carousels have painted and the shopper has had a
  // moment with the page, once per customer. Opening on top of a page they have
  // not seen yet reads as an interruption rather than an offer of help. New
  // shoppers arriving straight from the onboarding questions get the same
  // pause, so they are never handed two steppers back to back.
  useEffect(() => {
    if (!cdnMode || !result || isLoading || !tourTarget) return;
    if (hasSeenTour(shopifyCustomerId)) return;
    const timer = setTimeout(() => setIsTourRunning(true), 1600);
    return () => clearTimeout(timer);
  }, [cdnMode, result, isLoading, tourTarget, shopifyCustomerId]);

  // Finishing the tour arms the two questions; skipping does not, so opting out
  // stays a genuine opt-out. The prompt itself waits until the shopper has had
  // time with the page and started browsing it.
  const [isPromptArmed, setIsPromptArmed] = useState(false);

  const handlePrompt = useCallback(() => setFeedbackChoice('rate'), []);
  const noteBrowsing = useFeedbackPrompt({ armed: isPromptArmed, onPrompt: handlePrompt });

  // Volunteering feedback from the side tab replaces the timed ask.
  const handleFeedbackChoice = useCallback((choice: FeedbackChoice) => {
    setIsPromptArmed(false);
    setFeedbackChoice(choice);
  }, []);

  const handleTourFinish = (completed: boolean) => {
    setIsTourRunning(false);
    if (completed) setIsPromptArmed(true);
  };

  // Asking for the tour bypasses the once-per-shopper flag, which only guards
  // the unprompted opening.
  const handleReplayTour = useCallback(() => setIsTourRunning(true), []);

  // Back to the home page of the site the shopper is actually on — the domain
  // is the language, so a hardcoded one would strand French shoppers in English.
  const handleSignOut = async () => {
    try {
      await logoutCustomer();
    } finally {
      globalThis.location.href = getHomeUrl();
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-indigo-brand-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center">
              <IndigoLogo />
            </div>
            {cdnMode && (
              <div className="flex items-center gap-3 sm:gap-4">
                {customerFirstName && (
                  <span className="text-sm font-medium text-indigo-brand-text hidden sm:inline">
                    {t('header.greeting', { name: customerFirstName })}
                  </span>
                )}
                <a
                  href={getLanguageSwitchUrl()}
                  lang={t('header.languageSwitchLang')}
                  className="text-sm font-medium text-indigo-brand-text hover:text-indigo-brand-black underline underline-offset-4"
                >
                  {t('header.languageSwitch')}
                </a>
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="flex items-center gap-1.5 text-sm font-medium text-indigo-brand-text/60 hover:text-indigo-brand-black"
                >
                  <LogOut size={16} />
                  {t('header.signOut')}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content. On the CDN domains a right gutter keeps the fixed
          feedback tab clear of the carousel's next-slide arrow. */}
      <main
        className={`container mx-auto px-4 pt-4 pb-6 max-w-7xl flex-1 ${cdnMode ? 'sm:pr-14' : ''}`}
      >
        {/* Customer lookup — internal testing tool only. On the CDN domains
            the customer is resolved from their Shopify session instead. */}
        {!cdnMode && (
          <div className="bg-white border border-indigo-brand-border rounded-sm p-4 mb-6">
            <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-brand-text/40"
                  size={18}
                />
                <input
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('search.placeholder')}
                  className="w-full pl-10 pr-4 py-2.5 border border-indigo-brand-border focus:ring-2 focus:ring-indigo-brand-black/20 focus:border-indigo-brand-black text-base"
                  disabled={isLoading}
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isLoading || !email.trim()}
                className={`px-6 py-2.5 font-medium text-base transition-colors ${
                  isLoading || !email.trim()
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-indigo-brand-black text-white hover:bg-black'
                }`}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="animate-spin" size={18} />
                    {t('common.loading')}
                  </span>
                ) : (
                  t('search.button')
                )}
              </button>
            </form>

            {/* Resolved Shopify customer ID */}
            {shopifyCustomerId && (
              <div className="mt-4 flex items-center gap-2">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-brand-off-white border border-indigo-brand-border rounded-sm text-sm">
                  <User size={14} className="text-indigo-brand-text" />
                  <span className="text-indigo-brand-text/60">{t('search.shopifyIdLabel')}</span>
                  <span className="font-semibold text-indigo-brand-text">{shopifyCustomerId}</span>
                </span>
              </div>
            )}

            {/* Instructions */}
            {!hasSubmitted && !result && (
              <div className="mt-6 p-6 bg-indigo-brand-off-white rounded-sm">
                <h3 className="text-indigo-brand-text text-lg mb-3">
                  {t('search.howItWorksTitle')}
                </h3>
                <ol className="list-decimal list-inside text-base text-indigo-brand-text/70 space-y-2">
                  <li className="leading-relaxed">{t('search.step1')}</li>
                  <li className="leading-relaxed">{t('search.step2')}</li>
                  <li className="leading-relaxed">{t('search.step3')}</li>
                  <li className="leading-relaxed">{t('search.step4')}</li>
                </ol>
              </div>
            )}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-sm p-6 mb-8 flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-800">{t('common.error')}</h3>
              <p className="text-red-600">{error}</p>
            </div>
          </div>
        )}

        {/* Info Message (e.g. queued for async processing) */}
        {infoMessage && (
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-6 mb-8 flex items-start gap-4">
            <Info className="w-6 h-6 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-800">{t('status.processingTitle')}</h3>
              <p className="text-blue-600">{infoMessage}</p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="bg-white rounded-sm p-12 text-center">
            <Loader2 className="w-16 h-16 text-indigo-brand-black animate-spin mx-auto mb-4" />
            <h3 className="text-xl font-heading font-bold text-gray-800 mb-2">
              {t('status.generatingTitle')}
            </h3>
            <p className="text-gray-600">{t('status.generatingBody')}</p>
          </div>
        )}

        {/* Results Section */}
        {result && !isLoading && (
          <div className="bg-white overflow-hidden">
            {/* Tabs — hidden when a single view is left (customer-facing CDN) */}
            <div
              className={`border-b border-indigo-brand-border bg-indigo-brand-off-white ${
                showTabBar ? '' : 'hidden'
              }`}
            >
              <div className="flex overflow-x-auto">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      type="button"
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-medium transition-all whitespace-nowrap ${
                        activeTab === tab.id
                          ? 'border-b-2 border-indigo-brand-black text-indigo-brand-black bg-white'
                          : 'text-indigo-brand-text/60 hover:text-indigo-brand-black hover:bg-white/50'
                      }`}
                    >
                      <Icon size={18} />
                      {tab.label}
                      {tab.id === 'recommendations' &&
                        ((result.recommendations && result.recommendations.length > 0) ||
                          result.recommendations_by_profile) && (
                          <span className="ml-1 px-2 py-0.5 bg-indigo-brand-off-white text-indigo-brand-text text-xs rounded-sm border border-indigo-brand-border">
                            {result.recommendations && result.recommendations.length > 0
                              ? result.recommendations.length
                              : flattenRecommendationsByProfile(result.recommendations_by_profile)
                                  .length}
                          </span>
                        )}
                      {tab.id === 'history' && customerHistory.length > 0 && (
                        <span className="ml-1 px-2 py-0.5 bg-indigo-brand-off-white text-indigo-brand-text text-xs rounded-sm border border-indigo-brand-border">
                          {customerHistory.length}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tab Content — For You is denser (carousel rows), so less chrome padding */}
            <div className={activeTab === 'foryou' ? 'py-3 sm:py-4' : 'py-4 sm:py-6'}>
              {/* Customer Info Tab - Purchase Profiles */}
              {activeTab === 'customer' && (
                <div>
                  {/* Purchase Profiles */}
                  {result.purchase_profiles && result.purchase_profiles.length > 0 && (
                    <div>
                      <h3 className="text-xl font-heading font-bold mb-2 text-gray-800">
                        {t('profile.title')}
                      </h3>
                      <p className="text-gray-600 mb-4 text-sm">{t('profile.intro')}</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Sort profiles: dominant ones first */}
                        {[...result.purchase_profiles]
                          .sort((a: any, b: any) => {
                            const aIsDominant = result.dominant_profiles?.some(
                              (dp: any) => dp.name === a.name
                            );
                            const bIsDominant = result.dominant_profiles?.some(
                              (dp: any) => dp.name === b.name
                            );
                            if (aIsDominant && !bIsDominant) return -1;
                            if (!aIsDominant && bIsDominant) return 1;
                            return 0;
                          })
                          .map((profile: any, index: number) => {
                            const profileKey = profile.name || `profile-${index}`;
                            const isDominant =
                              result.dominant_profiles?.some(
                                (dp: any) => dp.name === profile.name
                              ) ?? false;
                            return (
                              <ProfileCard
                                key={profileKey}
                                profile={profile}
                                index={index}
                                isDominant={isDominant}
                                currentRating={profileRatings[profileKey]}
                                onRatingChange={handleProfileRatingChange}
                                books={getProfileBooks(profile)}
                              />
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* History Tab (internal testing only — hidden in CDN mode) */}
              {activeTab === 'history' && !cdnMode && (
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-2">
                    <h3 className="text-xl font-heading font-bold text-gray-800">
                      {t('history.title')}
                    </h3>
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <span className="text-sm text-gray-600">{t('history.purchasedOnly')}</span>
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={showPurchasedOnly}
                          onChange={(e) => setShowPurchasedOnly(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-10 h-5 bg-gray-200 rounded-full peer peer-checked:bg-green-500 transition-colors"></div>
                        <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5"></div>
                      </div>
                    </label>
                  </div>
                  <p className="text-gray-600 mb-6">{t('history.intro')}</p>
                  {customerHistoryLoading ? (
                    <div className="text-center py-12">
                      <Loader2 className="w-10 h-10 text-indigo-brand-black animate-spin mx-auto mb-3" />
                      <p className="text-gray-500">{t('history.loading')}</p>
                    </div>
                  ) : (
                    <BookGrid
                      books={getHistoryBooks()}
                      showRating={true}
                      onRatingChange={handleHistoryRatingChange}
                      ratings={historyRatings}
                      emptyMessage={t('history.empty')}
                      ratingQuestion={t('history.didYouLike')}
                      ratingYesLabel={t('common.yes')}
                      ratingNoLabel={t('common.no')}
                      showGiftQuestion={true}
                      onGiftStatusChange={handleGiftStatusChange}
                      giftStatus={giftStatus}
                      showPrices={false}
                      hideBadges={true}
                    />
                  )}
                </div>
              )}

              {/* Recommendations Tab (internal testing only — hidden in CDN mode) */}
              {activeTab === 'recommendations' && !cdnMode && (
                <div>
                  <h3 className="text-xl font-heading font-bold mb-2 text-gray-800">
                    {t('recommendations.title')}
                  </h3>
                  <p className="text-gray-600 mb-6">{t('recommendations.intro')}</p>
                  <BookGrid
                    books={getRecommendationBooks()}
                    showRating={true}
                    onRatingChange={handleRatingChange}
                    ratings={bookRatings}
                    emptyMessage={t('recommendations.empty')}
                    showPrices={false}
                    hideBadges={true}
                  />
                </div>
              )}

              {/* For You Tab - Profile Carousels */}
              {activeTab === 'foryou' && (
                <ProfileCarouselPage
                  results={result}
                  showPrices={false}
                  hideBadges={true}
                  showShelfActions
                  shopifyCustomerId={shopifyCustomerId}
                  tourTarget={cdnMode ? tourTarget : null}
                  onBrowse={noteBrowsing}
                />
              )}

              {/* Feedback Tab */}
              {activeTab === 'feedback' && (
                <div className="max-w-2xl mx-auto">
                  <h3 className="text-xl font-heading font-bold mb-2 text-gray-800">
                    {t('feedback.tabTitle')}
                  </h3>
                  <p className="text-gray-600 mb-6">{t('feedback.tabIntro')}</p>
                  <FeedbackForm
                    customerId={result.shopify_customer_id}
                    bookRatings={bookRatings}
                    books={getRecommendationBooks()}
                    historyRatings={historyRatings}
                    historyBooks={getHistoryBooks()}
                    giftStatus={giftStatus}
                    profileRatings={profileRatings}
                    profiles={result.purchase_profiles || []}
                    onSubmit={onFeedbackSubmit}
                    isSubmitting={feedbackSubmitting}
                    submitError={feedbackError}
                    submitSuccess={feedbackSuccess}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-indigo-brand-off-white border-t border-indigo-brand-border mt-auto">
        <div className="container mx-auto px-4 py-6 text-center text-indigo-brand-text/60">
          <p className="text-sm">{cdnMode ? t('footer.cdn') : t('footer.internal')}</p>
        </div>
      </footer>

      {/* Customer-facing feedback chrome: side tab, its dialog, and the tour. */}
      {cdnMode && result && !isLoading && (
        <>
          <FeedbackSideTab
            onSelect={handleFeedbackChoice}
            onReplayTour={tourTarget ? handleReplayTour : undefined}
          />

          {isTourRunning && tourTarget && (
            <OnboardingTour
              target={tourTarget}
              shopifyCustomerId={shopifyCustomerId}
              personalizationLevel={result.personalization_level}
              onFinish={handleTourFinish}
            />
          )}

          {feedbackChoice && (
            <ExperienceFeedbackModal
              mode={feedbackChoice}
              shopifyCustomerId={shopifyCustomerId}
              personalizationLevel={result.personalization_level}
              onClose={() => setFeedbackChoice(null)}
            />
          )}
        </>
      )}
    </div>
  );
};

export default UserTestingPage;
